import { COLORS } from '../design/tokens';
import { createRandom, toDataUri } from './seed';

const WIDTH = 800;
const HEIGHT = 1000;

export interface PortraitOptions {
  /** See the note on BackdropOptions.reducedMotion. */
  readonly reducedMotion: boolean;
}

/**
 * An abstract figure on a measuring grid.
 *
 * The prototype draws an initials monogram here. Dropped, for the reason given in
 * cover.ts: an SVG-as-image cannot load the page's self-hosted Inter, so the initials
 * would render in an arbitrary fallback face.
 */
export function generatePortrait({ reducedMotion }: PortraitOptions): string {
  const random = createRandom(777);
  const parts: string[] = [];

  const animations = reducedMotion
    ? ''
    : '<style>@keyframes pl{0%,100%{opacity:.35}50%{opacity:.9}}' +
      '.pd{animation:pl 4s ease-in-out infinite}</style>';

  parts.push(
    '<defs>' +
      '<linearGradient id="pbg" x1="0" y1="0" x2="0.6" y2="1">' +
      '<stop offset="0%" stop-color="#17151A"/>' +
      '<stop offset="100%" stop-color="#0B0B0D"/></linearGradient>' +
      '<radialGradient id="pglow" cx="0.5" cy="0.38" r="0.55">' +
      `<stop offset="0%" stop-color="${COLORS.accent}" stop-opacity="0.30"/>` +
      `<stop offset="100%" stop-color="${COLORS.accent}" stop-opacity="0"/></radialGradient>` +
      '<linearGradient id="pfig" x1="0" y1="0" x2="0" y2="1">' +
      `<stop offset="0%" stop-color="${COLORS.accent}" stop-opacity="0.42"/>` +
      `<stop offset="100%" stop-color="${COLORS.accent}" stop-opacity="0.10"/></linearGradient>` +
      animations +
      '</defs>',
  );

  parts.push(`<rect width="${WIDTH}" height="${HEIGHT}" fill="url(#pbg)"/>`);
  parts.push(`<rect width="${WIDTH}" height="${HEIGHT}" fill="url(#pglow)"/>`);

  for (let x = 0; x <= WIDTH; x += 40) {
    parts.push(
      `<line x1="${x}" y1="0" x2="${x}" y2="${HEIGHT}" stroke="${COLORS.text}" stroke-width="0.5" opacity="0.045"/>`,
    );
  }
  for (let y = 0; y <= HEIGHT; y += 40) {
    parts.push(
      `<line x1="0" y1="${y}" x2="${WIDTH}" y2="${y}" stroke="${COLORS.text}" stroke-width="0.5" opacity="0.045"/>`,
    );
  }

  const shoulders = 'M 190 1000 C 190 760 285 640 400 640 C 515 640 610 760 610 1000';

  parts.push('<circle cx="400" cy="392" r="132" fill="url(#pfig)"/>');
  parts.push(`<path d="${shoulders} Z" fill="url(#pfig)"/>`);
  parts.push(
    `<circle cx="400" cy="392" r="132" fill="none" stroke="${COLORS.accent}" stroke-width="1.4" opacity="0.5"/>`,
  );
  parts.push(
    `<path d="${shoulders}" fill="none" stroke="${COLORS.accent}" stroke-width="1.4" opacity="0.4"/>`,
  );

  const dotClass = reducedMotion ? '' : ' class="pd"';
  for (let index = 0; index < 26; index += 1) {
    const cx = (60 + random() * 680).toFixed(0);
    const cy = (60 + random() * 880).toFixed(0);
    const r = (1.6 + random() * 2.4).toFixed(1);
    const delay = reducedMotion
      ? ''
      : ` style="animation-delay:${(random() * 4).toFixed(1)}s"`;

    parts.push(
      `<circle${dotClass} cx="${cx}" cy="${cy}" r="${r}" fill="${COLORS.accent}" opacity="0.5"${delay}/>`,
    );
  }

  parts.push(
    `<rect x="1" y="1" width="${WIDTH - 2}" height="${HEIGHT - 2}" fill="none" stroke="${COLORS.accent}" opacity="0.2"/>`,
  );

  return toDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" ` +
      `width="${WIDTH}" height="${HEIGHT}" role="presentation">${parts.join('')}</svg>`,
  );
}
